import React, { useEffect, useState } from "react";
import { Loader2, Search, X } from "lucide-react";
import { format } from "date-fns";
import { toast } from "react-toastify";
import api from "~/services/api";
import { getData } from "~/functions/gridFunctions";
import ModalShell from "./ModalShell";
import {
  theme,
  field as fieldStyle,
  primaryButton,
  ghostButton,
  label,
} from "./modalStyles";

function Picker({ title, route, type, extraParams, value, onChange, describe }) {
  const [active, setActive] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  async function loadData(search = null) {
    setLoading(true);
    try {
      const data = await getData(route, {
        limit: 10,
        search: search ? { value: search } : null,
        type,
        extraParams,
      });
      setRows((data || []).filter(Boolean));
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (active) loadData();
  }, [active]);

  return (
    <div style={{ position: "relative", width: "100%" }}>
      <span style={label}>{title}</span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          width: "100%",
          marginTop: 4,
        }}
      >
        <button
          type="button"
          onClick={() => setActive(!active)}
          style={{ ...ghostButton, padding: 8, display: "flex" }}
        >
          {active ? <X size={14} /> : <Search size={14} />}
        </button>
        <div
          style={{
            ...fieldStyle,
            flex: 1,
            width: "auto",
            backgroundColor: theme.subtle,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {value ? describe(value) : "-"}
        </div>
      </div>
      {active ? (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            zIndex: 20,
            backgroundColor: theme.surface,
            border: `1px solid ${theme.border}`,
            borderRadius: 6,
            boxShadow: "0 10px 25px rgba(0,0,0,0.15)",
            width: "100%",
            maxHeight: 200,
            overflowY: "auto",
            marginTop: 4,
          }}
        >
          <input
            type="text"
            autoFocus
            placeholder="Search..."
            onChange={(event) => loadData(event.target.value)}
            style={{
              ...fieldStyle,
              border: "none",
              borderBottom: `1px solid ${theme.border}`,
              borderRadius: 0,
            }}
          />
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 8,
                padding: 12,
                color: theme.muted,
              }}
            >
              <Loader2 size={14} className="animate-spin" /> Loading...
            </div>
          ) : rows.length === 0 ? (
            <div
              style={{ padding: 12, color: theme.muted, textAlign: "center" }}
            >
              No results.
            </div>
          ) : (
            rows.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => {
                  onChange(r);
                  setActive(false);
                }}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: 8,
                  border: "none",
                  borderBottom: `1px solid ${theme.subtle}`,
                  backgroundColor: theme.surface,
                  color: theme.text,
                  fontSize: 12,
                  cursor: "pointer",
                }}
              >
                {describe(r)}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

export default function FeeModal({
  conciliationId,
  suggestedAmount = 0,
  onClose,
  onLaunched,
}) {
  const [amount, setAmount] = useState(
    suggestedAmount ? Number(suggestedAmount).toFixed(2) : ""
  );
  const [merchant, setMerchant] = useState(null);
  const [chartOfAccount, setChartOfAccount] = useState(null);
  const [costCenter, setCostCenter] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [dueDate, setDueDate] = useState(format(new Date(), "yyyy-MM-dd"));
  const [saving, setSaving] = useState(false);

  async function handleSubmit(event) {
    event.preventDefault();
    const parsedAmount = Number(String(amount).replace(",", "."));
    if (!parsedAmount || parsedAmount <= 0) {
      toast("Inform a valid amount.", { type: "warning", autoClose: 2000 });
      return;
    }
    if (!merchant || !chartOfAccount || !paymentMethod || !dueDate) {
      toast("Fill in all the required fields.", {
        type: "warning",
        autoClose: 2000,
      });
      return;
    }
    setSaving(true);
    try {
      await api.post(`/gravityconciliations/${conciliationId}/fee`, {
        amount: parsedAmount,
        merchant_id: merchant.id,
        chartofaccount_id: chartOfAccount.id,
        costcenter_id: costCenter?.id || null,
        paymentmethod_id: paymentMethod.id,
        due_date: dueDate,
      });
      toast("Fee launched!", { autoClose: 1000 });
      onLaunched();
      onClose();
    } catch (err) {
      toast(err?.response?.data?.error || "Could not launch the fee.", {
        type: "error",
        autoClose: 3000,
      });
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell title="Launch financial fee" onClose={onClose} maxWidth={420}>
      <form
        onSubmit={handleSubmit}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 16,
          padding: 16,
          overflowY: "auto",
          minHeight: 0,
        }}
      >
        <div style={{ width: "100%" }}>
          <span style={label}>Amount</span>
          <input
            type="text"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            style={{ ...fieldStyle, marginTop: 4 }}
          />
        </div>

        <Picker
          title="Merchant"
          route="merchants"
          value={merchant}
          onChange={setMerchant}
          describe={(r) => r.name}
        />

        <Picker
          title="Chart of Account"
          route="chartofaccounts"
          type="expenses"
          extraParams={merchant ? { merchant_id: merchant.id } : null}
          value={chartOfAccount}
          onChange={setChartOfAccount}
          describe={(r) => `${r.code || ""} ${r.name || ""}`.trim()}
        />

        <Picker
          title="Cost Center"
          route="costcenters"
          value={costCenter}
          onChange={setCostCenter}
          describe={(r) => `${r.code || ""} ${r.name || ""}`.trim()}
        />

        <Picker
          title="Payment Method"
          route="paymentmethods"
          value={paymentMethod}
          onChange={setPaymentMethod}
          describe={(r) => r.description}
        />

        <div style={{ width: "100%" }}>
          <span style={label}>Due Date</span>
          <input
            type="date"
            value={dueDate}
            onChange={(event) => setDueDate(event.target.value)}
            style={{ ...fieldStyle, marginTop: 4 }}
          />
        </div>

        <p style={label}>An account payable will be generated.</p>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "flex-end",
            gap: 8,
          }}
        >
          <button type="button" onClick={onClose} style={ghostButton}>
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            style={{ ...primaryButton, opacity: saving ? 0.5 : 1 }}
          >
            {saving ? <Loader2 size={14} className="animate-spin" /> : null}
            Launch
          </button>
        </div>
      </form>
    </ModalShell>
  );
}
